import { useState, useRef, useEffect } from "react";
import { Link, useNavigate, useLocation } from "react-router-dom";
import { toast } from "sonner";
import { Loader2, ArrowLeft, Mail } from "lucide-react";

import { api, setAuthToken } from "@/lib/api";
import { useAuth, formatApiError } from "@/lib/auth";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";

/**
 * Unified sign-in for everyone — clients, staff, owners.
 * Step 1: enter email.
 * Step 2: server decides — password (staff) or one-time code (client / passwordless accounts).
 * The UI never reveals which path the email belongs to until step 2 is rendered.
 */
export default function Login() {
    const { login, refreshMe } = useAuth();
    const nav = useNavigate();
    const loc = useLocation();
    const [step, setStep] = useState("email"); // email | password | otp
    const [email, setEmail] = useState(() => {
        // Pre-fill from ?email= (used by Signup's "Sign in instead" nudge),
        // falling back to the last email entered on this device.
        try {
            const q = new URLSearchParams(window.location.search);
            const fromQuery = q.get("email");
            if (fromQuery) return fromQuery.trim().toLowerCase();
            return localStorage.getItem("co_last_email") || "";
        } catch { return ""; }
    });
    const [password, setPassword] = useState("");
    const [code, setCode] = useState("");
    const [revealed, setRevealed] = useState(null); // { code, expires_at } when email delivery is down
    const [rememberDevice, setRememberDevice] = useState(true);
    const [busy, setBusy] = useState(false);
    const [err, setErr] = useState(null);
    const [resendIn, setResendIn] = useState(0);
    const tickRef = useRef(null);

    // Persist the last email locally so iOS keychain + our own pre-fill both work.
    function rememberEmailLocally(raw) {
        try {
            const v = (raw || "").trim().toLowerCase();
            if (v && v.includes("@")) localStorage.setItem("co_last_email", v);
        } catch { /* private mode — ignore */ }
    }

    useEffect(() => {
        if (resendIn <= 0) return undefined;
        tickRef.current = setInterval(() => setResendIn((s) => Math.max(0, s - 1)), 1000);
        return () => clearInterval(tickRef.current);
    }, [resendIn]);

    function landingFor(role) {
        return role === "client" ? "/portal" : "/app";
    }

    async function identify(e) {
        e?.preventDefault();
        if (!email) return;
        setErr(null);
        setBusy(true);
        rememberEmailLocally(email);
        try {
            const { data } = await api.post("/auth/otp/identify", { email: email.trim().toLowerCase() }, { withCredentials: true });
            if (data.method === "device_trusted") {
                // Server recognised the trusted device cookie — sign in directly.
                setAuthToken(data.token);
                await refreshMe();
                toast.success(`Welcome back, ${data.user.name || ""}`);
                nav(loc.state?.from?.pathname || landingFor(data.user.role), { replace: true });
                return;
            }
            if (data.method === "password") {
                setStep("password");
            } else {
                // OTP path — send the code immediately
                const { data: req } = await api.post("/auth/otp/request", { email: email.trim().toLowerCase() });
                if (req?.emergency_reveal && req.code) {
                    setRevealed({ code: req.code, expires_at: req.expires_at });
                    toast.success("Email delivery is paused — your code is shown below.");
                } else {
                    toast.success("If that email is on file, your code is on the way.");
                }
                setStep("otp");
                setResendIn(60);
            }
        } catch (e) { setErr(formatApiError(e)); }
        finally { setBusy(false); }
    }

    async function submitPassword(e) {
        e?.preventDefault();
        setErr(null);
        setBusy(true);
        try {
            const res = await login(email.trim().toLowerCase(), password);
            toast.success(`Welcome back, ${res.user.name}`);
            nav(loc.state?.from?.pathname || landingFor(res.user.role), { replace: true });
        } catch (e) { setErr(formatApiError(e)); }
        finally { setBusy(false); }
    }

    async function switchToOtp() {
        // Self-service rescue: any user (staff or client) can fall back to OTP.
        setErr(null);
        setBusy(true);
        try {
            const { data: req } = await api.post("/auth/otp/request", { email: email.trim().toLowerCase() });
            if (req?.emergency_reveal && req.code) {
                setRevealed({ code: req.code, expires_at: req.expires_at });
                toast.success("Email delivery is paused — your code is shown below.");
            } else {
                toast.success("If that email is on file, your code is on the way.");
            }
            setPassword("");
            setStep("otp");
            setResendIn(60);
        } catch (e) { setErr(formatApiError(e)); }
        finally { setBusy(false); }
    }

    async function submitCode(e) {
        e?.preventDefault();
        if (code.length !== 6) { setErr("Enter the 6-digit code"); return; }
        setErr(null);
        setBusy(true);
        try {
            const { data } = await api.post("/auth/otp/verify", {
                email: email.trim().toLowerCase(),
                code,
                remember_device: rememberDevice,
            }, { withCredentials: true });
            setAuthToken(data.token);
            await refreshMe();
            toast.success(`Welcome, ${data.user.name || "back"}${data.device_remembered ? ` · trusted for ${data.device_trust_days} days` : ""}`);
            nav(loc.state?.from?.pathname || landingFor(data.user.role), { replace: true });
        } catch (e) { setErr(formatApiError(e)); }
        finally { setBusy(false); }
    }

    async function resend() {
        if (resendIn > 0 || busy) return;
        setErr(null);
        setBusy(true);
        try {
            const { data: req } = await api.post("/auth/otp/request", { email: email.trim().toLowerCase() });
            if (req?.emergency_reveal && req.code) {
                setRevealed({ code: req.code, expires_at: req.expires_at });
                toast.success("Code re-generated (email paused)");
            } else {
                setRevealed(null);
                toast.success("Code re-sent");
            }
            setResendIn(60);
        } catch (e) { setErr(formatApiError(e)); }
        finally { setBusy(false); }
    }

    function reset() {
        setStep("email"); setPassword(""); setCode(""); setErr(null); setRevealed(null);
    }

    return (
        <div className="min-h-screen bg-[#F5F4EF] flex flex-col" data-testid="login-page">
            <header className="px-6 sm:px-10 pt-8">
                <Link to="/" className="inline-flex items-center gap-2 rounded-full border border-[#E4E4E7] bg-white px-3 py-1.5 text-xs text-[#5E636E] hover:text-[#0F1115] transition-colors" data-testid="login-back">
                    <ArrowLeft className="h-3.5 w-3.5" /> Back
                </Link>
            </header>

            <main className="flex-1 flex items-start justify-center px-6 pt-12 sm:pt-20">
                <div className="w-full max-w-md">
                    <div className="inline-flex items-center gap-3 mb-2" data-testid="login-brand">
                        <span className="grid h-10 w-10 place-items-center rounded-lg bg-[#0E3B2E] text-white font-display text-sm font-bold">NZ</span>
                        <span className="text-base font-medium">NZ Taxation</span>
                    </div>
                    <div className="mb-10 text-[11px] font-medium uppercase tracking-[0.14em] text-[#5E636E]" data-testid="login-trading-name">
                        NZ Taxation Limited t/a ClientOps
                    </div>

                    {step === "email" && (
                        <>
                            <div className="text-[11px] uppercase tracking-[0.18em] text-[#5E636E] mb-2">Sign in</div>
                            <h1 className="font-display text-3xl tracking-tight font-semibold">Welcome</h1>
                            <p className="mt-2 text-sm text-[#5E636E] leading-relaxed">Enter your email to continue.</p>
                            <form onSubmit={identify} className="mt-8 space-y-4" data-testid="login-email-form" method="post" action="#">
                                <div>
                                    <Label className="text-[11px] uppercase tracking-[0.14em] text-[#5E636E]" htmlFor="login-email">Email address</Label>
                                    <div className="relative mt-1.5">
                                        <Mail className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-[#5E636E]" />
                                        <Input
                                            id="login-email"
                                            name="email"
                                            type="email"
                                            inputMode="email"
                                            autoComplete="username"
                                            autoCorrect="off"
                                            autoCapitalize="none"
                                            spellCheck="false"
                                            enterKeyHint="next"
                                            placeholder="you@example.co.nz"
                                            value={email}
                                            onChange={(e) => setEmail(e.target.value)}
                                            onBlur={(e) => rememberEmailLocally(e.target.value)}
                                            required
                                            className="pl-9 h-12 text-base"
                                            data-testid="login-email-input"
                                            autoFocus={!email}
                                        />
                                    </div>
                                </div>
                                {err && <div className="rounded-md border border-rose-200 bg-rose-50 px-3 py-2 text-xs text-rose-700" data-testid="login-error">{err}</div>}
                                <Button
                                    type="submit"
                                    className="w-full h-12 bg-[#0E3B2E] hover:bg-[#0B2F25] text-white font-medium text-base"
                                    disabled={busy || !email}
                                    data-testid="login-continue"
                                >
                                    {busy ? <><Loader2 className="h-4 w-4 animate-spin" /> Continuing…</> : "Continue"}
                                </Button>
                            </form>
                        </>
                    )}

                    {step === "password" && (
                        <>
                            <div className="text-[11px] uppercase tracking-[0.18em] text-[#5E636E] mb-2">Sign in</div>
                            <h1 className="font-display text-3xl tracking-tight font-semibold">Enter your password</h1>
                            <p className="mt-2 text-sm text-[#5E636E] leading-relaxed">Signing in as <span className="font-medium text-[#0F1115]">{email}</span>.</p>
                            <form onSubmit={submitPassword} className="mt-8 space-y-4" data-testid="login-password-form" method="post" action="#">
                                {/* Hidden username field — iOS Keychain links this with the password so it can save the credential pair */}
                                <input type="email" name="email" autoComplete="username" value={email} readOnly className="sr-only" tabIndex={-1} aria-hidden="true" />
                                <div>
                                    <Label className="text-[11px] uppercase tracking-[0.14em] text-[#5E636E]" htmlFor="login-password">Password</Label>
                                    <Input
                                        id="login-password"
                                        name="password"
                                        type="password"
                                        autoComplete="current-password"
                                        placeholder="••••••••"
                                        value={password}
                                        onChange={(e) => setPassword(e.target.value)}
                                        required
                                        className="mt-1.5 h-12 text-base"
                                        data-testid="login-password-input"
                                        autoFocus
                                    />
                                </div>
                                {err && <div className="rounded-md border border-rose-200 bg-rose-50 px-3 py-2 text-xs text-rose-700" data-testid="login-error">{err}</div>}
                                <Button
                                    type="submit"
                                    className="w-full h-12 bg-[#0E3B2E] hover:bg-[#0B2F25] text-white font-medium text-base"
                                    disabled={busy || !password}
                                    data-testid="login-submit-password"
                                >
                                    {busy ? <><Loader2 className="h-4 w-4 animate-spin" /> Signing in…</> : "Sign in"}
                                </Button>
                            </form>
                            <button
                                type="button"
                                onClick={reset}
                                className="mt-6 text-xs text-[#5E636E] hover:text-[#0F1115] underline underline-offset-4"
                                data-testid="login-change-email"
                            >
                                Use a different email
                            </button>
                            <button
                                type="button"
                                onClick={switchToOtp}
                                disabled={busy}
                                className="mt-3 block text-xs text-[#0E3B2E] hover:text-[#0B2F25] disabled:opacity-50 underline underline-offset-4"
                                data-testid="login-forgot-password"
                            >
                                Forgot password? Email me a one-time code instead →
                            </button>
                        </>
                    )}

                    {step === "otp" && (
                        <>
                            <div className="text-[11px] uppercase tracking-[0.18em] text-[#5E636E] mb-2">Sign in</div>
                            <h1 className="font-display text-3xl tracking-tight font-semibold">Enter your code</h1>
                            <p className="mt-2 text-sm text-[#5E636E] leading-relaxed">
                                We sent a 6-digit code to <span className="font-medium text-[#0F1115]">{email}</span>. It expires in 10 minutes.
                            </p>
                            {revealed && (
                                <div className="mt-5 rounded-lg border-2 border-amber-300 bg-amber-50 p-4" data-testid="login-code-revealed">
                                    <div className="text-[11px] font-medium uppercase tracking-[0.14em] text-amber-900">
                                        Email delivery is paused — your code
                                    </div>
                                    <div className="mt-2 font-mono text-3xl font-bold tracking-[0.4em] text-[#0F1115]">
                                        {revealed.code}
                                    </div>
                                    <div className="mt-2 text-xs text-amber-900">
                                        We couldn't reach the mailbox so your code is shown here. Enter it below to sign in.
                                    </div>
                                </div>
                            )}
                            <form onSubmit={submitCode} className="mt-8 space-y-4" data-testid="login-code-form">
                                <div>
                                    <Label className="text-[11px] uppercase tracking-[0.14em] text-[#5E636E]">One-time code</Label>
                                    <Input
                                        type="text"
                                        inputMode="numeric"
                                        pattern="[0-9]*"
                                        autoComplete="one-time-code"
                                        maxLength={6}
                                        placeholder="••••••"
                                        value={code}
                                        onChange={(e) => setCode(e.target.value.replace(/\D/g, "").slice(0, 6))}
                                        autoFocus
                                        className="mt-1.5 h-14 text-center text-2xl tracking-[0.6em] font-mono"
                                        data-testid="login-code-input"
                                    />
                                </div>
                                {err && <div className="rounded-md border border-rose-200 bg-rose-50 px-3 py-2 text-xs text-rose-700" data-testid="login-error">{err}</div>}
                                <label className="flex items-start gap-2 rounded-md bg-[#F5F4EF] border border-[#E4E4E7] px-3 py-2.5 cursor-pointer hover:border-[#0E3B2E]/40 transition-colors" data-testid="login-remember-device-toggle">
                                    <input
                                        type="checkbox"
                                        checked={rememberDevice}
                                        onChange={(e) => setRememberDevice(e.target.checked)}
                                        className="mt-0.5 accent-[#0E3B2E]"
                                        data-testid="login-remember-device"
                                    />
                                    <span className="text-xs leading-relaxed">
                                        <span className="font-medium text-[#0F1115]">Trust this device for 30 days</span>
                                        <span className="block text-[#5E636E]">Skip the code next time on this browser. Don&apos;t tick this on shared or public devices.</span>
                                    </span>
                                </label>
                                <Button
                                    type="submit"
                                    className="w-full h-12 bg-[#0E3B2E] hover:bg-[#0B2F25] text-white font-medium text-base"
                                    disabled={busy || code.length !== 6}
                                    data-testid="login-submit-code"
                                >
                                    {busy ? <><Loader2 className="h-4 w-4 animate-spin" /> Verifying…</> : "Sign in"}
                                </Button>
                            </form>
                            <div className="mt-6 flex items-center justify-between text-xs">
                                <button
                                    type="button"
                                    onClick={reset}
                                    className="text-[#5E636E] hover:text-[#0F1115] underline underline-offset-4"
                                    data-testid="login-change-email"
                                >
                                    Use a different email
                                </button>
                                <button
                                    type="button"
                                    onClick={resend}
                                    disabled={resendIn > 0 || busy}
                                    className="text-[#0E3B2E] hover:text-[#0B2F25] disabled:text-[#5E636E] disabled:cursor-not-allowed underline underline-offset-4"
                                    data-testid="login-resend"
                                >
                                    {resendIn > 0 ? `Resend in ${resendIn}s` : "Resend code"}
                                </button>
                            </div>
                        </>
                    )}
                </div>
            </main>
        </div>
    );
}
